import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { KeyRound, Plus, Trash2, Edit, Copy, RefreshCw, ExternalLink, CheckCircle2, Search, X, ChevronDown } from "lucide-react";

// Autocompletar de proyectos: input con búsqueda + dropdown de coincidencias.
// Escalable a miles de proyectos.
const ProjectAutocomplete = ({ projects, value, onChange, testid }) => {
  const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const selected = projects.find((p) => p.id === value) || null;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const matches = useMemo(() => {
    const q = norm(query.trim());
    if (!projects?.length) return [];
    const list = q
      ? projects.filter((p) => norm(`${p.nombre} ${p.cliente_nombre || ""}`).includes(q))
      : projects;
    return list.slice(0, 40);
  }, [projects, query]);

  const displayValue = selected ? `${selected.nombre}${selected.cliente_nombre ? ` — ${selected.cliente_nombre}` : ""}` : "";

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Input
          value={open ? query : displayValue}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(""); }}
          placeholder="Busca por nombre o cliente…"
          className="pr-8"
          data-testid={testid}
          autoComplete="off"
        />
        {selected && !open ? (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500"
            onClick={(e) => { e.preventDefault(); onChange(""); setQuery(""); }}
            title="Limpiar selección"
          >
            <X className="w-4 h-4" />
          </button>
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        )}
      </div>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {matches.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-slate-500">
              {projects.length === 0 ? "No hay proyectos activos" : "Sin coincidencias"}
            </div>
          ) : (
            matches.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { onChange(p.id); setOpen(false); setQuery(""); }}
                className={`w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 last:border-b-0 ${p.id === value ? "bg-blue-50" : ""}`}
              >
                <div className="text-sm text-slate-800">{p.nombre}</div>
                {p.cliente_nombre && <div className="text-[11px] text-slate-500">{p.cliente_nombre}</div>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const FormulariosAccesos = () => {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filtros de la tabla
  const [q, setQ] = useState("");
  const [fEstado, setFEstado] = useState("all");   // all | active | inactive
  const [fTipo, setFTipo] = useState("all");       // all | solicitante | admin
  const [fEmpresa, setFEmpresa] = useState("all"); // all | <empresa>
  const [fPerfil, setFPerfil] = useState("all");   // all | pendiente | completado | none
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    nombre_completo: "", email: "", empresa: "", client_id: "",
    expires_at: "", is_active: true, must_change_password: false,
    perfil_viajero_required: false, perfil_viajero_project_id: "",
    user_type: "solicitante",
    allowed_services: [], allowed_flight_classes: [], allowed_hotel_chains: [],
    user_is_default_passenger: false, max_passengers: 0,
    requires_approval: false, notify_admin_ids: [],
  });
  const [createdPassword, setCreatedPassword] = useState(null);
  const [pwDialog, setPwDialog] = useState(null); // { id, password }
  const [confirmRegen, setConfirmRegen] = useState(null); // { id, email, nombre }
  const [confirmRegenMustChange, setConfirmRegenMustChange] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  // Admins de solicitudes disponibles para la empresa seleccionada (multi-select
  // "Informar solicitudes a"). Se recarga al cambiar empresa o abrir el diálogo.
  const [availableAdmins, setAvailableAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  // Cadenas hoteleras únicas del catálogo de Hoteles (columna chain_name)
  const [hotelChains, setHotelChains] = useState([]);
  const [chainSearch, setChainSearch] = useState("");

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const [r, cs, ps, ch] = await Promise.all([
        axios.get(`${API}/form-access`, { withCredentials: true }),
        axios.get(`${API}/clients`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/viajeros-ext/projects`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/hotels/chains`, { withCredentials: true }).catch(() => ({ data: { chains: [] } })),
      ]);
      setRows(r.data || []);
      setClients(cs.data || []);
      // Sólo proyectos activos y no archivados
      const validProjects = (ps.data || []).filter(p => p.activo !== false && (p.project_status || "activo") === "activo");
      setProjects(validProjects);
      setHotelChains(ch.data?.chains || []);
    } catch (err) {
      toast.error("Error al cargar accesos");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const reset = () => {
    setForm({
      nombre_completo: "", email: "", empresa: "", client_id: "",
      expires_at: "", is_active: true, must_change_password: false,
      perfil_viajero_required: false, perfil_viajero_project_id: "",
      user_type: "solicitante",
      allowed_services: [], allowed_flight_classes: [], allowed_hotel_chains: [],
      user_is_default_passenger: false, max_passengers: 0,
      requires_approval: false, notify_admin_ids: [],
    });
    setEditing(null);
    setCreatedPassword(null);
    setAvailableAdmins([]);
  };

  const openNew = () => { reset(); setShowDialog(true); };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      nombre_completo: row.nombre_completo || "",
      email: row.email || "",
      empresa: row.empresa || "",
      client_id: row.client_id || "",
      expires_at: row.expires_at ? row.expires_at.slice(0, 10) : "",
      is_active: row.is_active !== false,
      must_change_password: !!row.must_change_password,
      perfil_viajero_required: !!row.perfil_viajero_required,
      perfil_viajero_project_id: row.perfil_viajero_project_id || "",
      user_type: row.user_type || "solicitante",
      allowed_services: row.allowed_services || [],
      allowed_flight_classes: row.allowed_flight_classes || [],
      allowed_hotel_chains: row.allowed_hotel_chains || [],
      user_is_default_passenger: !!row.user_is_default_passenger,
      max_passengers: Number(row.max_passengers || 0),
      requires_approval: !!row.requires_approval,
      notify_admin_ids: row.notify_admin_ids || [],
    });
    setCreatedPassword(null);
    setShowDialog(true);
  };

  // Carga los admins de solicitudes disponibles para la empresa actual del form.
  // Sólo se cargan cuando el acceso es solicitante Y tiene `requires_approval`
  // marcado (el multi-select se muestra únicamente en ese caso).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!showDialog) return;
      if (form.user_type !== "solicitante" || !form.requires_approval) { setAvailableAdmins([]); return; }
      const emp = (form.empresa || "").trim();
      if (!emp) { setAvailableAdmins([]); return; }
      try {
        setLoadingAdmins(true);
        const r = await axios.get(`${API}/form-access/admins-solicitudes`, {
          params: { empresa: emp },
          withCredentials: true,
        });
        if (!cancelled) {
          // Excluye el propio acceso si estamos editando
          const list = (r.data || []).filter((a) => !editing || a.id !== editing.id);
          setAvailableAdmins(list);
        }
      } catch (_) {
        if (!cancelled) setAvailableAdmins([]);
      } finally {
        if (!cancelled) setLoadingAdmins(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showDialog, form.empresa, form.user_type, form.requires_approval, editing]);

  const handleSave = async () => {
    if (!form.nombre_completo || !form.email || !form.empresa || !form.client_id) {
      toast.error("Elige un cliente y completa nombre y email");
      return;
    }
    if (form.perfil_viajero_required && !form.perfil_viajero_project_id) {
      toast.error("Debes elegir un proyecto de Perfil de Viajero");
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/form-access/${editing.id}`, {
          ...form,
          expires_at: form.expires_at ? `${form.expires_at}T23:59:59.999Z` : null,
        }, { withCredentials: true });
        toast.success("Acceso actualizado");
        setShowDialog(false);
        fetchRows();
      } else {
        const res = await axios.post(`${API}/form-access`, {
          ...form,
          expires_at: form.expires_at ? `${form.expires_at}T23:59:59.999Z` : null,
        }, { withCredentials: true });
        setCreatedPassword({ ...res.data, must_change_password: !!form.must_change_password });
        toast.success("Acceso creado");
        fetchRows();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este acceso?")) return;
    try {
      await axios.delete(`${API}/form-access/${id}`, { withCredentials: true });
      toast.success("Acceso eliminado");
      fetchRows();
    } catch { toast.error("Error"); }
  };

  const regenPassword = async (row) => {
    // Open confirmation dialog instead of window.confirm (more reliable across browsers)
    setConfirmRegenMustChange(!!row.must_change_password);
    setConfirmRegen({ id: row.id, email: row.email, nombre: row.nombre_completo });
  };

  const doRegenPassword = async () => {
    if (!confirmRegen) return;
    setRegenLoading(true);
    try {
      const res = await axios.post(
        `${API}/form-access/${confirmRegen.id}/regenerate-password`,
        { must_change_password: confirmRegenMustChange },
        { withCredentials: true }
      );
      const newPw = res.data.password_plain;
      setPwDialog({ id: confirmRegen.id, email: confirmRegen.email, password: newPw, must_change_password: confirmRegenMustChange });
      // Persistent toast as backup so user can copy even if dialog gets dismissed
      toast.success(`Nueva contraseña: ${newPw}`, { duration: 30000 });
      setConfirmRegen(null);
      fetchRows();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Error desconocido";
      toast.error(`No se pudo regenerar: ${detail}`);
      console.error("regenPassword error:", err?.response?.status, err?.response?.data);
    } finally {
      setRegenLoading(false);
    }
  };

  const copyToClipboard = (txt) => {
    const fallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = txt;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) toast.success("Copiado");
        else toast.info("Selecciona y copia el texto manualmente");
      } catch {
        // Show the text in a prompt as last resort
        try { window.prompt("Copia este texto manualmente (Ctrl+C):", txt); } catch { /* ignore */ }
      }
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(txt).then(
        () => toast.success("Copiado"),
        () => fallback(),
      );
    } else {
      fallback();
    }
  };

  const baseUrl = window.location.origin;
  const fullUrl = (slug) => `${baseUrl}/cliente/${slug}`;

  // Listado único de empresas para el select de filtro
  const empresas = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.empresa && s.add(r.empresa));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  // Aplica filtros en cliente
  const filteredRows = useMemo(() => {
    const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const qq = norm(q.trim());
    return rows.filter((r) => {
      if (fEstado === "active" && !r.is_active) return false;
      if (fEstado === "inactive" && r.is_active) return false;
      if (fTipo === "solicitante" && (r.user_type || "solicitante") !== "solicitante") return false;
      if (fTipo === "admin" && r.user_type !== "administrador_solicitudes") return false;
      if (fEmpresa !== "all" && r.empresa !== fEmpresa) return false;
      if (fPerfil === "none" && r.perfil_viajero_required) return false;
      if (fPerfil === "pendiente" && !(r.perfil_viajero_required && !r.perfil_viajero_id)) return false;
      if (fPerfil === "completado" && !(r.perfil_viajero_required && r.perfil_viajero_id)) return false;
      if (qq) {
        const hay = [r.empresa, r.nombre_completo, r.email, r.slug].map(norm).join(" ");
        if (!hay.includes(qq)) return false;
      }
      return true;
    });
  }, [rows, q, fEstado, fTipo, fEmpresa, fPerfil]);

  const anyFilter = q || fEstado !== "all" || fTipo !== "all" || fEmpresa !== "all" || fPerfil !== "all";
  const clearFilters = () => { setQ(""); setFEstado("all"); setFTipo("all"); setFEmpresa("all"); setFPerfil("all"); };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="formularios-accesos-page">
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <KeyRound className="w-6 h-6" /> Administrar accesos
            </h1>
            <p className="text-slate-500 text-sm">Gestiona las credenciales de clientes externos que llenan formularios.</p>
          </div>
          <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-access-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo acceso
          </Button>
        </div>

        {/* Filtros */}
        <Card>
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por empresa, nombre, email o slug…"
                className="pl-8 h-9"
                data-testid="access-search"
              />
            </div>
            <Select value={fEstado} onValueChange={setFEstado}>
              <SelectTrigger className="w-[140px] h-9" data-testid="access-filter-estado"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Estado: todos</SelectItem>
                <SelectItem value="active">Activo</SelectItem>
                <SelectItem value="inactive">Inactivo</SelectItem>
              </SelectContent>
            </Select>
            <Select value={fTipo} onValueChange={setFTipo}>
              <SelectTrigger className="w-[180px] h-9" data-testid="access-filter-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tipo: todos</SelectItem>
                <SelectItem value="solicitante">Solicitante</SelectItem>
                <SelectItem value="admin">Admin de Solicitudes</SelectItem>
              </SelectContent>
            </Select>
            <Select value={fEmpresa} onValueChange={setFEmpresa}>
              <SelectTrigger className="w-[180px] h-9" data-testid="access-filter-empresa"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="all">Empresa: todas</SelectItem>
                {empresas.map((e) => (<SelectItem key={e} value={e}>{e}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={fPerfil} onValueChange={setFPerfil}>
              <SelectTrigger className="w-[180px] h-9" data-testid="access-filter-perfil"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Perfil viajero: todos</SelectItem>
                <SelectItem value="pendiente">Pendiente</SelectItem>
                <SelectItem value="completado">Completado</SelectItem>
                <SelectItem value="none">No requerido</SelectItem>
              </SelectContent>
            </Select>
            {anyFilter && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-600" data-testid="access-filter-clear">
                <X className="w-4 h-4 mr-1" /> Limpiar
              </Button>
            )}
            <span className="text-xs text-slate-500 ml-auto">
              {filteredRows.length} / {rows.length}
            </span>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">Empresa</TableHead>
                  <TableHead className="whitespace-nowrap">Nombre</TableHead>
                  <TableHead className="whitespace-nowrap">Email</TableHead>
                  <TableHead className="whitespace-nowrap">URL / Slug</TableHead>
                  <TableHead className="whitespace-nowrap">Expira</TableHead>
                  <TableHead className="whitespace-nowrap">Estado</TableHead>
                  <TableHead className="text-right whitespace-nowrap">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Cargando...</TableCell></TableRow>
                ) : filteredRows.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">
                    {rows.length === 0 ? "Sin accesos creados todavía" : "Ningún acceso coincide con los filtros"}
                  </TableCell></TableRow>
                ) : filteredRows.map((r) => (
                  <TableRow key={r.id} data-testid={`access-row-${r.id}`} className="[&>td]:py-1.5">
                    <TableCell className="font-medium whitespace-nowrap max-w-[180px] truncate" title={r.empresa}>{r.empresa}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap max-w-[180px] truncate" title={r.nombre_completo}>{r.nombre_completo}</TableCell>
                    <TableCell className="text-sm text-slate-600 whitespace-nowrap max-w-[220px] truncate" title={r.email}>{r.email}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <code className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono" title={fullUrl(r.slug)}>/{r.slug}</code>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(fullUrl(r.slug))} title="Copiar URL">
                          <Copy className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => window.open(fullUrl(r.slug), "_blank")} title="Abrir">
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                      {r.expires_at ? r.expires_at.slice(0, 10) : "Sin exp."}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <div className="flex items-center gap-1 flex-nowrap">
                        {r.is_active ? (
                          <Badge className="bg-green-100 text-green-700">Activo</Badge>
                        ) : (
                          <Badge className="bg-slate-200 text-slate-700">Inactivo</Badge>
                        )}
                        {r.user_type === "administrador_solicitudes" && (
                          <Badge className="bg-purple-100 text-purple-700 border border-purple-200" title="Administrador de Solicitudes">Admin Sol.</Badge>
                        )}
                        {r.perfil_viajero_required && (
                          <Badge className={`border ${r.perfil_viajero_id ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}
                            title={r.perfil_viajero_id ? "Perfil de viajero completado" : "Perfil de viajero pendiente"}
                          >
                            Perfil {r.perfil_viajero_id ? "OK" : "pend."}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => regenPassword(r)} title="Regenerar contraseña" data-testid={`regen-${r.id}`}>
                        <RefreshCw className="w-4 h-4 text-amber-600" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(r)} title="Editar">
                        <Edit className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(r.id)} title="Eliminar">
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={showDialog} onOpenChange={(o) => { if (!o) { setShowDialog(false); reset(); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar acceso" : "Nuevo acceso"}</DialogTitle>
          </DialogHeader>

          {createdPassword ? (
            <>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-emerald-800 font-semibold">Acceso creado correctamente</p>
                    <p className="text-xs text-emerald-700">Comparte estos datos con el cliente. La contraseña solo se muestra una vez.</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">URL</span>
                    <code className="bg-white border border-slate-200 px-2 py-1.5 rounded text-xs break-all">{fullUrl(createdPassword.slug)}</code>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Usuario</span>
                    <code className="bg-white border border-slate-200 px-2 py-1.5 rounded text-xs">{createdPassword.email}</code>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Contraseña</span>
                    <code className="bg-white border border-slate-200 px-2 py-1.5 rounded text-sm font-mono text-red-700">{createdPassword.password_plain}</code>
                  </div>
                </div>
                {createdPassword.must_change_password && (
                  <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                    <span className="font-semibold">Contraseña temporal:</span>
                    <span>el usuario deberá cambiarla en su primer inicio de sesión.</span>
                  </div>
                )}
                <Button
                  size="sm" variant="outline" className="w-full"
                  onClick={() => copyToClipboard(`URL: ${fullUrl(createdPassword.slug)}\nUsuario: ${createdPassword.email}\nContraseña: ${createdPassword.password_plain}${createdPassword.must_change_password ? "\n(Contraseña temporal · el usuario deberá cambiarla)" : ""}`)}
                >
                  <Copy className="w-3 h-3 mr-1.5" /> Copiar todo
                </Button>
              </div>
              <DialogFooter className="mt-4">
                <Button onClick={() => { setShowDialog(false); reset(); }} data-testid="access-created-listo">Listo</Button>
              </DialogFooter>
            </>
          ) : (
            <div className="space-y-3">
              <div>
                <Label>Empresa (cliente)</Label>
                <Select value={form.client_id || ""} onValueChange={(v) => {
                  const c = clients.find((x) => x.id === v);
                  setForm({ ...form, client_id: v, empresa: c?.company_name || "" });
                }}>
                  <SelectTrigger data-testid="access-empresa"><SelectValue placeholder="Elige una empresa" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.company_name}{c.folio ? <span className="text-slate-500 ml-1">- {c.folio}</span> : null}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {clients.length === 0 && (
                  <p className="mt-2 text-xs text-amber-700 italic">
                    No hay clientes cargados. Crea un cliente en el módulo <b>Clientes</b> antes de generar accesos.
                  </p>
                )}
              </div>
              <div>
                <Label>Nombre completo</Label>
                <Input value={form.nombre_completo} onChange={(e) => setForm({ ...form, nombre_completo: e.target.value })} data-testid="access-nombre" />
              </div>
              <div>
                <Label>Email (usuario)</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="access-email" />
              </div>
              <div>
                <Label>Fecha de expiración (opcional)</Label>
                <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
              </div>
              {editing && (
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} id="is_active" />
                  <Label htmlFor="is_active" className="cursor-pointer">Activo</Label>
                </div>
              )}
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 flex items-start gap-2.5">
                <input
                  type="checkbox"
                  id="must_change_password"
                  className="mt-1"
                  checked={form.must_change_password}
                  onChange={(e) => setForm({ ...form, must_change_password: e.target.checked })}
                  data-testid="access-must-change-pw"
                />
                <Label htmlFor="must_change_password" className="cursor-pointer text-sm leading-tight">
                  <span className="font-semibold text-slate-800">Contraseña temporal</span>
                  <span className="block text-xs text-slate-500 mt-0.5">
                    Obligar al usuario a cambiar la contraseña en su primer inicio de sesión.
                  </span>
                </Label>
              </div>

              {/* Tipo de usuario */}
              <div className="rounded-md border border-slate-200 p-3">
                <Label className="text-sm font-semibold text-slate-800 block mb-2">Tipo de usuario</Label>
                <div className="flex flex-col gap-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="user_type"
                      value="solicitante"
                      className="mt-1"
                      checked={form.user_type === "solicitante"}
                      onChange={() => setForm({ ...form, user_type: "solicitante" })}
                      data-testid="access-user-type-solicitante"
                    />
                    <span className="text-sm leading-tight">
                      <span className="font-medium text-slate-800">Solicitante</span>
                      <span className="block text-xs text-slate-500 mt-0.5">
                        Puede crear solicitudes de cotización. Cada solicitud genera ticket automáticamente.
                      </span>
                    </span>
                  </label>

                  {/* Requiere autorización de solicitud (solo si es Solicitante) */}
                  {form.user_type === "solicitante" && (
                    <div className="ml-6 rounded-md border border-amber-200 bg-amber-50/50 p-3 space-y-2">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={!!form.requires_approval}
                          onChange={(e) => setForm({
                            ...form,
                            requires_approval: e.target.checked,
                            notify_admin_ids: e.target.checked ? form.notify_admin_ids : [],
                          })}
                          data-testid="access-requires-approval"
                        />
                        <span className="text-sm leading-tight">
                          <span className="font-medium text-slate-800">Requiere autorización de solicitud</span>
                          <span className="block text-xs text-slate-500 mt-0.5">
                            Al enviar una cotización, el ticket queda <b>pendiente</b> hasta que un administrador de solicitudes de la misma empresa la apruebe. Si está desactivado, la solicitud pasa directo a Helpdesk.
                          </span>
                        </span>
                      </label>

                      {form.requires_approval && (
                        <div className="pl-6 space-y-1.5">
                          <Label className="text-xs text-slate-700 font-semibold uppercase tracking-wide">
                            Informar solicitudes a
                          </Label>
                          {!form.empresa ? (
                            <p className="text-xs text-slate-500 italic">Elige primero una empresa para ver los administradores disponibles.</p>
                          ) : loadingAdmins ? (
                            <p className="text-xs text-slate-500 italic">Cargando administradores…</p>
                          ) : availableAdmins.length === 0 ? (
                            <p className="text-xs text-amber-700 italic">
                              No hay usuarios tipo <b>Administrador de Solicitudes</b> activos para <b>{form.empresa}</b>. Crea uno antes de activar esta opción, o desactívala para que la solicitud vaya directo a Helpdesk.
                            </p>
                          ) : (
                            <div className="space-y-1 max-h-40 overflow-y-auto rounded border border-slate-200 bg-white p-2" data-testid="notify-admins-list">
                              {availableAdmins.map((a) => {
                                const checked = (form.notify_admin_ids || []).includes(a.id);
                                return (
                                  <label key={a.id} className="flex items-center gap-2 text-sm cursor-pointer py-0.5">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) => {
                                        const cur = form.notify_admin_ids || [];
                                        const next = e.target.checked
                                          ? Array.from(new Set([...cur, a.id]))
                                          : cur.filter((x) => x !== a.id);
                                        setForm({ ...form, notify_admin_ids: next });
                                      }}
                                      data-testid={`notify-admin-${a.id}`}
                                    />
                                    <span className="flex-1">
                                      <span className="font-medium text-slate-800">{a.nombre_completo || a.email}</span>
                                      {a.email && <span className="text-xs text-slate-500 ml-2">{a.email}</span>}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                          <p className="text-[11px] text-slate-500 mt-1">
                            Cada administrador recibirá un correo <b>individual</b> con su propio link de acceso (no verán el link de los demás).
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="user_type"
                      value="administrador_solicitudes"
                      className="mt-1"
                      checked={form.user_type === "administrador_solicitudes"}
                      onChange={() => setForm({ ...form, user_type: "administrador_solicitudes" })}
                      data-testid="access-user-type-admin"
                    />
                    <span className="text-sm leading-tight">
                      <span className="font-medium text-slate-800">Administrador de Solicitudes</span>
                      <span className="block text-xs text-slate-500 mt-0.5">
                        Recibe y aprueba/cancela las solicitudes de los solicitantes de su misma empresa antes de que se genere el ticket.
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              {/* Perfil de viajero requerido */}
              <div className="rounded-md border border-indigo-200 bg-indigo-50/50 p-3">
                <div className="flex items-start gap-2.5">
                  <input
                    type="checkbox"
                    id="perfil_viajero_required"
                    className="mt-1"
                    checked={form.perfil_viajero_required}
                    onChange={(e) => setForm({
                      ...form,
                      perfil_viajero_required: e.target.checked,
                      perfil_viajero_project_id: e.target.checked ? form.perfil_viajero_project_id : "",
                    })}
                    data-testid="access-perfil-viajero-required"
                  />
                  <Label htmlFor="perfil_viajero_required" className="cursor-pointer text-sm leading-tight">
                    <span className="font-semibold text-slate-800">Solicitar Perfil de Viajero</span>
                    <span className="block text-xs text-slate-500 mt-0.5">
                      Al primer inicio de sesión el usuario deberá completar el formulario público del proyecto elegido antes de poder cotizar.
                    </span>
                  </Label>
                </div>
                {form.perfil_viajero_required && (
                  <div className="mt-3 pl-6 space-y-2">
                    <Label className="text-xs text-slate-700 font-semibold uppercase tracking-wide">Proyecto</Label>
                    <ProjectAutocomplete
                      projects={projects}
                      value={form.perfil_viajero_project_id}
                      onChange={(v) => setForm({ ...form, perfil_viajero_project_id: v })}
                      testid="access-perfil-viajero-project"
                    />
                    {(() => {
                      const proj = projects.find(p => p.id === form.perfil_viajero_project_id);
                      if (!proj) return null;
                      const url = `${window.location.origin}/viajero/${proj.slug}`;
                      return (
                        <div className="rounded border border-indigo-300 bg-white p-2 flex items-center justify-between gap-2 min-w-0">
                          <div className="min-w-0 flex-1">
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700">Link público del formulario</div>
                            <a href={url} target="_blank" rel="noreferrer" className="text-xs text-indigo-700 hover:text-indigo-900 break-all block" data-testid="access-perfil-viajero-preview-link">
                              {url}
                            </a>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs shrink-0"
                            onClick={() => {
                              navigator.clipboard.writeText(url);
                              toast.success("Link copiado");
                            }}
                          >
                            <Copy className="w-3 h-3 mr-1" />Copiar
                          </Button>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
              {/* Configuración del formulario (Fase 3) */}
              <div className="rounded-md border border-blue-200 bg-blue-50/40 p-3 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Configuración del formulario de cotización</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Opcional. Deja vacío para permitir todos los valores. Selecciona sólo los que quieras habilitar para este acceso.
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-slate-700 font-semibold uppercase tracking-wide">Servicios permitidos</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {[
                      { k: "flight",    l: "Vuelos" },
                      { k: "hotel",     l: "Hotel" },
                      { k: "transfer",  l: "Traslados" },
                      { k: "car",       l: "Renta de auto" },
                      { k: "insurance", l: "Seguro" },
                      { k: "activity",  l: "Actividades" },
                    ].map(({ k, l }) => {
                      const active = (form.allowed_services || []).includes(k);
                      return (
                        <button
                          key={k}
                          type="button"
                          onClick={() => {
                            const cur = form.allowed_services || [];
                            const next = cur.includes(k) ? cur.filter(x => x !== k) : [...cur, k];
                            setForm({ ...form, allowed_services: next });
                          }}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${active ? "border-blue-600 bg-blue-100 text-blue-800" : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"}`}
                          data-testid={`access-svc-${k}`}
                        >{l}</button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-700 font-semibold uppercase tracking-wide">Clases de vuelo permitidas</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {["Economy Class", "Business Class", "First Class"].map((c) => {
                      const active = (form.allowed_flight_classes || []).includes(c);
                      return (
                        <button
                          key={c}
                          type="button"
                          onClick={() => {
                            const cur = form.allowed_flight_classes || [];
                            const next = cur.includes(c) ? cur.filter(x => x !== c) : [...cur, c];
                            setForm({ ...form, allowed_flight_classes: next });
                          }}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${active ? "border-indigo-600 bg-indigo-100 text-indigo-800" : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"}`}
                          data-testid={`access-class-${c.split(" ")[0].toLowerCase()}`}
                        >{c}</button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs text-slate-700 font-semibold uppercase tracking-wide">Cadenas hoteleras permitidas</Label>
                    <span className="text-[10px] text-slate-500">
                      {(form.allowed_hotel_chains || []).length} de {hotelChains.length + 1}
                    </span>
                  </div>
                  {hotelChains.length > 8 && (
                    <div className="relative mb-1.5">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                      <input
                        type="text"
                        value={chainSearch}
                        onChange={(e) => setChainSearch(e.target.value)}
                        placeholder="Buscar cadena hotelera…"
                        className="w-full pl-7 pr-2 py-1 text-xs border border-slate-200 rounded bg-white focus:outline-none focus:border-indigo-400"
                        data-testid="access-hotel-chain-search"
                      />
                      {chainSearch && (
                        <button
                          type="button"
                          onClick={() => setChainSearch("")}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                          data-testid="access-hotel-chain-search-clear"
                        ><X className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-1 mt-1 max-h-40 overflow-y-auto p-2 bg-white rounded border border-slate-200">
                    {(() => {
                      const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                      const q = norm(chainSearch.trim());
                      // Siempre incluir "Sin Preferencia" al principio + cadenas del catálogo
                      const list = ["Sin Preferencia", ...hotelChains];
                      const filtered = q ? list.filter((c) => norm(c).includes(q)) : list;
                      if (!filtered.length) {
                        return <p className="col-span-2 text-xs text-slate-400 text-center py-3">Sin coincidencias</p>;
                      }
                      return filtered.map((c) => {
                        const active = (form.allowed_hotel_chains || []).includes(c);
                        return (
                          <label key={c} className="flex items-center gap-1.5 text-xs cursor-pointer">
                            <input
                              type="checkbox"
                              checked={active}
                              onChange={() => {
                                const cur = form.allowed_hotel_chains || [];
                                const next = cur.includes(c) ? cur.filter(x => x !== c) : [...cur, c];
                                setForm({ ...form, allowed_hotel_chains: next });
                              }}
                              data-testid={`access-hotel-chain-${c.split(" ")[0].toLowerCase()}`}
                            />
                            <span className={active ? "text-slate-800 font-medium" : "text-slate-600"}>{c}</span>
                          </label>
                        );
                      });
                    })()}
                  </div>
                  {hotelChains.length === 0 && (
                    <p className="text-[11px] text-slate-500 mt-1">
                      El catálogo de Hoteles está vacío. Agrega cadenas en <b>Configuración → Hoteles</b> para poder seleccionarlas aquí.
                    </p>
                  )}
                </div>
                <div className="flex items-start gap-2.5 p-2 rounded bg-white border border-slate-200">
                  <input
                    type="checkbox"
                    id="user_is_default_passenger"
                    className="mt-1"
                    checked={!!form.user_is_default_passenger}
                    onChange={(e) => setForm({ ...form, user_is_default_passenger: e.target.checked })}
                    data-testid="access-user-is-passenger"
                  />
                  <Label htmlFor="user_is_default_passenger" className="cursor-pointer text-sm leading-tight">
                    <span className="font-semibold text-slate-800">El usuario es el pasajero por defecto</span>
                    <span className="block text-xs text-slate-500 mt-0.5">
                      Muestra un checkbox &ldquo;Yo soy uno de los pasajeros&rdquo; que autollena el pasajero #1 con su nombre y correo.
                    </span>
                  </Label>
                </div>
                <div className="p-2 rounded bg-white border border-slate-200">
                  <Label htmlFor="max_passengers" className="text-xs text-slate-700 font-semibold uppercase tracking-wide">
                    Máximo de pasajeros por solicitud
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      id="max_passengers"
                      type="number"
                      min={0}
                      max={99}
                      step={1}
                      value={form.max_passengers ?? 0}
                      onChange={(e) => setForm({ ...form, max_passengers: Math.max(0, parseInt(e.target.value || "0", 10) || 0) })}
                      className="w-28"
                      data-testid="access-max-passengers"
                    />
                    <span className="text-xs text-slate-500">
                      0 = sin límite. El solicitante no podrá capturar más pasajeros que este valor.
                    </span>
                  </div>
                </div>
              </div>

              {!editing && (
                <p className="text-xs text-slate-500">La contraseña se generará automáticamente y se mostrará al guardar.</p>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => { setShowDialog(false); reset(); }}>Cancelar</Button>
                <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-access-btn">
                  {editing ? "Guardar" : "Crear acceso"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm regenerate password */}
      <Dialog open={!!confirmRegen} onOpenChange={(o) => { if (!o && !regenLoading) setConfirmRegen(null); }}>
        <DialogContent data-testid="pw-regen-confirm">
          <DialogHeader><DialogTitle>Regenerar contraseña</DialogTitle></DialogHeader>
          {confirmRegen && (
            <div className="space-y-3">
              <p className="text-sm text-slate-700">
                ¿Regenerar la contraseña de <strong>{confirmRegen.nombre || confirmRegen.email}</strong> (<code className="text-xs">{confirmRegen.email}</code>)?
              </p>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5 rounded">
                ⚠ La contraseña anterior dejará de funcionar inmediatamente. Esta nueva contraseña solo se mostrará una vez.
              </p>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 flex items-start gap-2.5">
                <input
                  type="checkbox"
                  id="regen_must_change"
                  className="mt-1"
                  checked={confirmRegenMustChange}
                  onChange={(e) => setConfirmRegenMustChange(e.target.checked)}
                  disabled={regenLoading}
                  data-testid="regen-must-change-pw"
                />
                <Label htmlFor="regen_must_change" className="cursor-pointer text-sm leading-tight">
                  <span className="font-semibold text-slate-800">Marcar como contraseña temporal</span>
                  <span className="block text-xs text-slate-500 mt-0.5">
                    El usuario deberá cambiarla en su siguiente inicio de sesión.
                  </span>
                </Label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmRegen(null)} disabled={regenLoading}>
                  Cancelar
                </Button>
                <Button
                  onClick={doRegenPassword}
                  className="bg-amber-600 hover:bg-amber-700"
                  disabled={regenLoading}
                  data-testid="pw-regen-confirm-btn"
                >
                  {regenLoading ? "Generando..." : "Sí, regenerar"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Regenerate password dialog */}
      <Dialog open={!!pwDialog} onOpenChange={(o) => { if (!o) setPwDialog(null); }}>
        <DialogContent data-testid="pw-regen-dialog">
          <DialogHeader><DialogTitle>Nueva contraseña generada</DialogTitle></DialogHeader>
          {pwDialog && (
            <div className="space-y-3">
              <p className="text-sm">Nueva contraseña para <strong>{pwDialog.email}</strong>:</p>
              <input
                readOnly
                value={pwDialog.password}
                onClick={(e) => e.target.select()}
                onFocus={(e) => e.target.select()}
                autoFocus
                className="block w-full bg-slate-100 px-3 py-2 rounded text-red-700 text-lg text-center font-mono border border-slate-300 focus:ring-2 focus:ring-blue-400"
                data-testid="pw-regen-value"
              />
              <Button variant="outline" size="sm" onClick={() => copyToClipboard(pwDialog.password)} data-testid="pw-regen-copy">
                <Copy className="w-3 h-3 mr-1" /> Copiar al portapapeles
              </Button>
              {pwDialog.must_change_password && (
                <div className="text-xs text-blue-800 bg-blue-50 border border-blue-200 px-3 py-2 rounded">
                  <strong>Contraseña temporal:</strong> el usuario deberá cambiarla en su siguiente inicio de sesión.
                </div>
              )}
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5 rounded">
                ⚠ Esta contraseña solo se muestra una vez. Cópiala antes de cerrar este diálogo.
              </p>
              <DialogFooter><Button onClick={() => setPwDialog(null)}>Listo</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FormulariosAccesos;
